import makeStyles from '@mui/styles/makeStyles';
import {color, font, radius, type} from "common/tokens";
import AnswerText from "components/QuizAnswers/AnswerText";
import {useSetTranslation} from "common/useSetTranslation";
import {FileText} from "lucide-react";
import {filter, get, includes, isArray, isEmpty, join, map} from "lodash";
import React from "react";

// One person's reply, rendered small enough to sit on a single row of the
// answer card. The question type decides the shape of the value, not the
// weight it is given — every reply reads at the same size.
const useStyles = makeStyles({
  images: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    margin: 0,
  },
  image: {
    display: 'block',
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: radius.CONTROL,
  },
  document: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    color: color.TEXT,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    wordBreak: 'break-all',
  },
  documentIcon: {
    color: color.INFO,
    flexShrink: 0,
    display: 'flex',
  },
});

// The stored answer keeps option keys; the reader needs the labels.
const labelsForKeys = (question, answer, st) => {
  const options = get(question, 'answerOptions', []);
  const keys = isArray(answer) ? answer : [answer];
  const chosen = filter(options, option => includes(keys, option.key));
  return join(map(chosen, option => st(option.label, option.localeLabel)), ', ');
};

const scaleValue = (question, answer, st) => {
  const options = get(question, 'scaleQuestionnaire.options', []);
  const picked = isArray(answer) ? get(answer, '[0]') : answer;
  const match = filter(options, option => option.option === picked);
  const label = isEmpty(match) ? '' : st(get(match, '[0].label'), get(match, '[0].localeLabel'));
  return label ? `${picked} — ${label}` : `${picked}`;
};

const fileNameOf = url => {
  const path = String(url).split('?')[0];
  return decodeURIComponent(path.split('/').pop() || path);
};

const AnswerValue = ({question, quizAnswer}) => {
  const classes = useStyles();
  const {st} = useSetTranslation();

  const answer = get(quizAnswer, 'answer');
  const questionType = get(question, 'questionType');

  switch (questionType) {
    case 'SingleChoiceRadio':
    case 'SingleChoiceDropDown':
    case 'SingleChoiceButton':
    case 'MultiChoiceDropDown':
      return <AnswerText>{labelsForKeys(question, answer, st)}</AnswerText>;

    case 'Scale':
      return <AnswerText>{scaleValue(question, answer, st)}</AnswerText>;

    case 'AgreeabilityThreeOptions':
    case 'AgreeabilityFiveOptions':
    case 'AgreeabilitySevenScale':
    case 'AgreeabilityTenScale':
      return <AnswerText>{isArray(answer) ? join(answer, ', ') : answer}</AnswerText>;

    case 'ImageResponse':
      return (
        <span className={classes.images}>
          {map(isArray(answer) ? answer : [answer], (src, index) => (
            <a key={index} href={src} target={'_blank'} rel={'noopener noreferrer'}>
              <img className={classes.image} src={src} alt={''}/>
            </a>
          ))}
        </span>
      );

    case 'Document':
      return (
        <a className={classes.document} href={answer} target={'_blank'} rel={'noopener noreferrer'}>
          <span className={classes.documentIcon}><FileText size={25} strokeWidth={2}/></span>
          {fileNameOf(answer)}
        </a>
      );

    default:
      // Text, Number, TextBox, Date and anything new the backend adds — all of
      // them come back as a plain value worth showing as it is.
      return <AnswerText>{isArray(answer) ? join(answer, ', ') : answer}</AnswerText>;
  }
};

export default AnswerValue;
